import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { messageCount, patientCount } from '../bulkVocabulary';
import type { SubjectLanguage } from '../types';

export interface BulkLaunchConfirmDialogProps {
  open: boolean;
  requirementLabel: string;
  eligiblePatients: number;
  messagesToSend: number;
  subject: SubjectLanguage;
  isLaunching: boolean;
  /** Set when the previous attempt failed. Retrying re-sends the SAME launch. */
  errorMessage?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The confirm step before a bulk send (AC-12).
 *
 * It exists for two reasons and the second is not cosmetic. The first is the
 * ordinary one: a send to a hundred-odd people should take a deliberate second
 * action. The second is AC-13 — opening this dialog is what mints the
 * idempotency key, so every click inside it, including a retry after a
 * failure, belongs to one launch.
 *
 * That is why a failed attempt leaves the dialog OPEN with the error on it
 * rather than closing and asking the admin to start again: starting again is
 * precisely what would mint a second key, and a first attempt that landed
 * before it timed out would then send twice.
 */
export const BulkLaunchConfirmDialog = ({
  open,
  requirementLabel,
  eligiblePatients,
  messagesToSend,
  subject,
  isLaunching,
  errorMessage,
  onConfirm,
  onCancel,
}: BulkLaunchConfirmDialogProps) => (
  <Dialog open={open} onClose={isLaunching ? undefined : onCancel} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700 }}>Send these requests?</DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        {requirementLabel} — {patientCount(eligiblePatients, subject.plural, subject.singular)},{' '}
        {messageCount(messagesToSend)}.
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
        Messages go out straight away. You can stop the ones that have not been sent yet.
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage} Trying again re-sends this same launch, so nobody is messaged twice.
        </Alert>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={isLaunching} sx={{ textTransform: 'none' }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={isLaunching}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {isLaunching ? 'Sending…' : errorMessage ? 'Try again' : 'Send'}
      </Button>
    </DialogActions>
  </Dialog>
);
